import { forwardRef, FunctionComponent, PropsWithoutRef, ReactNode, Ref } from 'react';
import styled, { css } from 'styled-components';
import { hideVisually, readableColor, rgba } from 'polished';

import { BaseProps, ForwardProps } from '../../types';
import { defaultThemeProp } from '../../theme';
import { tryCatch } from '../../utils';
import { calculateFontSize, FontSize } from '../../styles';

export interface LabelProps extends BaseProps {
  /**
   * Id for the label.
   * @default undefined
   */
  id?: string;
  /**
   * HTML tag to render the label as.
   * @default "label"
   */
  as?: 'label' | 'legend' | 'div' | 'span';
  /** Use to associate with a form-element's id. */
  htmlFor?: string;
  /**
   * Visually hides the label.
   * @default false
   */
  labelHidden?: boolean;
  /** Content/text for the label. */
  children: ReactNode;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLLabelElement | HTMLLegendElement | HTMLDivElement | HTMLSpanElement>;
}

export const StyledLabel = styled.label<Pick<LabelProps, 'labelHidden' | 'htmlFor'>>(
  ({ theme, labelHidden }) => {
    const color = tryCatch(() => {
      return rgba(
        readableColor(theme.base.palette['primary-background']),
        theme.base.transparency['transparent-3']
      );
    });

    const fontSize = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);

    return labelHidden
      ? hideVisually
      : css`
          max-width: max-content;
          font-size: ${fontSize[theme.components.label['font-size'] as FontSize]};
          font-weight: ${theme.base['font-weight']['semi-bold']};
          color: ${color};
        `;
  }
);

StyledLabel.defaultProps = defaultThemeProp;

const Label: FunctionComponent<LabelProps & ForwardProps> = forwardRef(
  (
    { children, labelHidden = false, htmlFor, ...restProps }: PropsWithoutRef<LabelProps>,
    ref: LabelProps['ref']
  ) => {
    return (
      <StyledLabel
        ref={ref as Ref<HTMLLabelElement>}
        labelHidden={labelHidden}
        htmlFor={htmlFor === '' ? undefined : htmlFor}
        {...restProps}
      >
        {children}
      </StyledLabel>
    );
  }
);

export default Label;
